import { Artifact } from '@frontend/types/specialized-agent';
import { StepExecution } from '@frontend/types/stepExecution';

export type GroupedArtifactExecution = {
  agentId: string;
  agentName: string;
  stepExecutionId: string;
  artifacts: Artifact[];
  executionTime?: number;
};

export type GroupedArtifactsByAgent = {
  [agentId: string]: GroupedArtifactExecution[];
};

/**
 * Recursively processes a step execution and its children to group artifacts by agent
 * @param step - Step execution to process
 * @param grouped - Accumulated grouped artifacts object
 */
function processStepExecutionRecursively(
  step: StepExecution,
  grouped: GroupedArtifactsByAgent
): void {
  // Process artifacts from the current step execution
  if (step.execution?.artifacts && step.execution.artifacts.length > 0) {
    const agentId = step.agent?.agent_id || 'unknown';
    const agentName = step.agent?.name || 'Unknown Agent';
    const stepExecutionId = step.id || '';

    if (!grouped[agentId]) {
      grouped[agentId] = [];
    }

    grouped[agentId].push({
      agentId,
      agentName,
      stepExecutionId,
      artifacts: step.execution.artifacts,
      executionTime: step.execution_time,
    });
  }

  // Recursively process children (ForLoop iterations, nested steps, etc.)
  if (step.children && step.children.length > 0) {
    step.children.forEach(child => {
      processStepExecutionRecursively(child, grouped);
    });
  }
}

/**
 * Groups artifacts from step executions by agent ID
 * Recursively processes nested executions (e.g., ForLoop iterations)
 * @param stepExecutions - Array of step executions from workflow execution
 * @returns Object with agent IDs as keys and their artifacts as values
 */
export function groupArtifactsByAgent(
  stepExecutions: StepExecution[] | unknown[]
): GroupedArtifactsByAgent {
  const grouped: GroupedArtifactsByAgent = {};

  // Type guard to check if stepExecutions is properly typed
  if (!Array.isArray(stepExecutions)) {
    return grouped;
  }

  stepExecutions.forEach((stepExecution: StepExecution | unknown) => {
    // Type guard for StepExecution
    const step = stepExecution as StepExecution;
    processStepExecutionRecursively(step, grouped);
  });

  return grouped;
}

/**
 * Counts total number of artifacts across all agents
 * @param groupedArtifacts - Grouped artifacts by agent
 * @returns Total count of artifacts
 */
export function getTotalArtifactCount(groupedArtifacts: GroupedArtifactsByAgent): number {
  return Object.values(groupedArtifacts).reduce(
    (total, executions) =>
      total + executions.reduce((sum, execution) => sum + execution.artifacts.length, 0),
    0
  );
}

/**
 * Checks if there are any artifacts in the grouped structure
 * @param groupedArtifacts - Grouped artifacts by agent
 * @returns True if there are any artifacts
 */
export function hasAnyArtifacts(groupedArtifacts: GroupedArtifactsByAgent): boolean {
  return Object.keys(groupedArtifacts).length > 0;
}

/**
 * Recursively collects all artifacts from a step execution and its children
 * @param step - Step execution to process
 * @param allArtifacts - Accumulated array of artifacts
 */
function collectArtifactsRecursively(step: StepExecution, allArtifacts: Artifact[]): void {
  // Collect artifacts from the current step execution
  if (step.execution?.artifacts && step.execution.artifacts.length > 0) {
    allArtifacts.push(...step.execution.artifacts);
  }

  // Recursively process children (ForLoop iterations, nested steps, etc.)
  if (step.children && step.children.length > 0) {
    step.children.forEach(child => {
      collectArtifactsRecursively(child, allArtifacts);
    });
  }
}

/**
 * Flattens all artifacts from step executions into a single array
 * Recursively processes nested executions (e.g., ForLoop iterations)
 * @param stepExecutions - Array of step executions from workflow execution
 * @returns Flat array of all artifacts
 */
export function flattenArtifacts(stepExecutions: StepExecution[] | unknown[]): Artifact[] {
  const allArtifacts: Artifact[] = [];

  // Type guard to check if stepExecutions is properly typed
  if (!Array.isArray(stepExecutions)) {
    return allArtifacts;
  }

  stepExecutions.forEach((stepExecution: StepExecution | unknown) => {
    // Type guard for StepExecution
    const step = stepExecution as StepExecution;
    collectArtifactsRecursively(step, allArtifacts);
  });

  return allArtifacts;
}
